'use client'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { clearPendingReferral } from '@/hooks/use-register-user'
import { authFetch } from '@/lib/auth/auth-fetch'
import { useInviteStore } from '@/stores/invite-store'
import { Lock } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useState } from 'react'

export function InviteCodeModal() {
	const [code, setCode] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const t = useTranslations('inviteRequired')

	const { needsInviteCode, hideInviteModal } = useInviteStore()

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError(null)

		const trimmedCode = code.trim().toUpperCase()
		if (!trimmedCode) {
			setError(t('errors.empty'))
			return
		}

		setIsSubmitting(true)

		try {
			// Complete registration with the invite code. authFetch attaches the
			// Privy auth + identity headers so the server resolves the wallet itself.
			const res = await authFetch('/api/auth/register', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					referralCode: trimmedCode,
				}),
			})

			const data = await res.json()

			if (!res.ok) {
				if (data.code === 'invalid_code') {
					setError(t('errors.invalid'))
				} else if (data.code === 'code_exhausted') {
					setError(t('errors.exhausted'))
				} else {
					setError(t('errors.failed'))
				}
				setIsSubmitting(false)
				return
			}

			// Success - user is now registered
			clearPendingReferral()
			hideInviteModal()
			setCode('')
			setError(null)
			setIsSubmitting(false)

			// Reload to ensure the app state is fresh
			window.location.reload()
		} catch (err) {
			console.error('Error completing registration:', err)
			setError(t('errors.failed'))
			setIsSubmitting(false)
		}
	}

	return (
		<Dialog open={needsInviteCode} onOpenChange={() => {}}>
			<DialogContent
				className="max-w-md border-white/10 bg-brand-dark"
				onPointerDownOutside={e => e.preventDefault()}
				onEscapeKeyDown={e => e.preventDefault()}
				showCloseButton={false}
			>
				<div className="flex flex-col items-center gap-4 text-center">
					<div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-blue/10">
						<Lock className="h-8 w-8 text-brand-blue" />
					</div>
					<Image src="/logos/logo.png" alt="Chance" width={120} height={32} className="h-auto" />
					<DialogTitle className="text-white text-xl">{t('title')}</DialogTitle>
					<DialogDescription className="text-white/60">{t('description')}</DialogDescription>
				</div>

				<form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
					<Input
						value={code}
						onChange={e => setCode(e.target.value.toUpperCase())}
						placeholder={t('placeholder')}
						className="h-12 border-white/10 bg-white/5 text-center font-mono text-lg text-white uppercase placeholder:text-white/30"
						autoFocus
					/>

					{error && <p className="overflow-hidden text-center text-red-400 text-sm">{error}</p>}

					<Button
						type="submit"
						disabled={isSubmitting || !code.trim()}
						className="h-12 bg-brand-blue font-semibold text-base text-white hover:bg-brand-blue/90 disabled:opacity-50"
					>
						{isSubmitting ? t('validating') : t('continue')}
					</Button>
				</form>

				<p className="mt-2 text-center text-sm text-white/40">{t('helpText')}</p>
			</DialogContent>
		</Dialog>
	)
}
